import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { catalogApi } from "@/lib/apiClient";
import { formatMoney, taxNote } from "@/lib/format";
import { CATALOG } from "@/constants/testIds";

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
  { value: "name", label: "Name (A–Z)" },
];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "featured";
  const productType = params.get("product_type") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));

  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, has_more: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    catalogApi.categories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    catalogApi
      .products({ q: q || undefined, category: category || undefined, product_type: productType || undefined, sort, page, page_size: 24 })
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(e.response?.data?.error?.message || "Failed to load products.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [q, category, productType, sort, page]);

  const updateParam = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) next.delete(k);
      else next.set(k, String(v));
    });
    // Any filter change resets to page 1
    if (Object.keys(patch).some((k) => k !== "page")) next.delete("page");
    setParams(next, { replace: true });
  };

  const onSearch = (e) => {
    e.preventDefault();
    updateParam({ q: searchInput.trim() });
  };

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === category),
    [categories, category],
  );

  return (
    <div className="min-h-screen bg-background" data-testid={CATALOG.root}>
      <header className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3 sm:gap-6">
          <Button
            asChild
            size="sm"
            variant="ghost"
            className="-ml-2"
            data-testid="catalog-back-btn"
          >
            <Link to="/">← Back</Link>
          </Button>
          <Link to="/" className="font-semibold whitespace-nowrap">
            FAST FORCE
          </Link>
          <form onSubmit={onSearch} className="flex-1 flex gap-2 max-w-xl">
            <Input
              type="search"
              placeholder="Search by name, SKU or brand"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              data-testid={CATALOG.searchInput}
            />
            <Button type="submit" variant="secondary" data-testid={CATALOG.searchSubmit}>
              Search
            </Button>
          </form>
          <Link to="/admin/login" className="text-sm text-muted-foreground hover:text-foreground whitespace-nowrap">
            Admin
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 grid gap-8 md:grid-cols-[240px_1fr]">
        {/* Sidebar */}
        <aside>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Categories
          </div>
          <ul className="space-y-1 text-sm">
            <li>
              <button
                type="button"
                className={`w-full text-left px-2 py-1.5 rounded ${!category ? "bg-black text-white" : "hover:bg-muted"}`}
                onClick={() => updateParam({ category: "" })}
                data-testid={CATALOG.categoryFilterAll}
              >
                All products
              </button>
            </li>
            {categories.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={`w-full text-left px-2 py-1.5 rounded ${category === c.slug ? "bg-black text-white" : "hover:bg-muted"}`}
                  onClick={() => updateParam({ category: c.slug })}
                  data-testid={CATALOG.categoryFilterItem}
                >
                  {c.name}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Main */}
        <main>
          <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-semibold">
                {activeCategory ? activeCategory.name : q ? `Results for "${q}"` : "All products"}
              </h1>
              <div className="text-sm text-muted-foreground mt-1">
                {loading ? "Loading…" : `${data.total} ${data.total === 1 ? "item" : "items"}`}
              </div>
            </div>
            <Select value={sort} onValueChange={(v) => updateParam({ sort: v })}>
              <SelectTrigger className="w-56" data-testid={CATALOG.sortSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Product type filter — Genuine vs Aftermarket */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="catalog-type-filter">
            {[
              { value: "", label: "All" },
              { value: "genuine", label: "Genuine / OEM" },
              { value: "aftermarket", label: "Aftermarket" },
            ].map((opt) => (
              <button
                key={opt.value || "all"}
                type="button"
                onClick={() => updateParam({ product_type: opt.value })}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  productType === opt.value
                    ? "bg-black text-white border-black"
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                }`}
                data-testid={`catalog-type-${opt.value || "all"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="text-sm text-destructive border border-destructive/40 rounded p-3 mb-6">
              {error}
            </div>
          )}

          {!loading && data.items.length === 0 && (
            <div
              className="border rounded p-12 text-center text-sm text-muted-foreground"
              data-testid={CATALOG.emptyState}
            >
              No products match your filters.
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid={CATALOG.productGrid}>
            {data.items.map((p) => (
              <Link
                to={`/product/${p.slug}`}
                key={p.id}
                className="group border rounded-lg overflow-hidden bg-white hover:shadow-sm transition-shadow"
                data-testid={CATALOG.productCard}
              >
                <div className="aspect-square bg-muted overflow-hidden">
                  {p.main_image && (
                    <img
                      src={p.main_image}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                    />
                  )}
                </div>
                <div className="p-3 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    {p.brand && (
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground truncate">
                        {p.brand}
                      </div>
                    )}
                    <Badge
                      variant={p.product_type === "genuine" ? "default" : "outline"}
                      className={p.product_type === "genuine" ? "bg-emerald-600 hover:bg-emerald-600 text-[10px] px-1.5" : "text-[10px] px-1.5"}
                    >
                      {p.product_type === "genuine" ? "Genuine" : "Aftermarket"}
                    </Badge>
                  </div>
                  <div
                    className="text-sm font-medium line-clamp-2 min-h-[2.5rem]"
                    data-testid={CATALOG.productCardName}
                  >
                    {p.name}
                  </div>
                  <div className="flex items-baseline gap-2 pt-1">
                    <div className="font-semibold" data-testid={CATALOG.productCardPrice}>
                      {formatMoney(p.price)}
                    </div>
                    {p.compare_at_price && (
                      <div className="text-xs text-muted-foreground line-through">
                        {formatMoney(p.compare_at_price)}
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {taxNote(p.tax_inclusive, p.vat_rate)}
                  </div>
                  {!p.in_stock && (
                    <Badge variant="destructive" className="mt-1 gap-1" data-testid="catalog-card-unavail">
                      <span className="inline-flex w-4 h-4 rounded-full bg-white text-red-600 items-center justify-center text-[10px] font-bold">!</span>
                      Not available
                    </Badge>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {(data.total > data.page_size) && (
            <div className="flex justify-between items-center mt-8" data-testid={CATALOG.pagination}>
              <Button
                variant="outline"
                disabled={page <= 1}
                onClick={() => updateParam({ page: page - 1 })}
                data-testid={CATALOG.prevPageBtn}
              >
                Previous
              </Button>
              <div className="text-sm text-muted-foreground">
                Page {data.page}
              </div>
              <Button
                variant="outline"
                disabled={!data.has_more}
                onClick={() => updateParam({ page: page + 1 })}
                data-testid={CATALOG.nextPageBtn}
              >
                Next
              </Button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
